import React, { useState, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { EngagementEntryDiagram } from '../components/executive';

const CONTACT_EMAIL = 'info@sharedoxygen.com';
const RECAPTCHA_SITE_KEY =
  import.meta.env.VITE_RECAPTCHA_SITE_KEY || '6LdS10stAAAAANAZZ51b5rzHOC0oETYJOIVaVLCQ';
const RECAPTCHA_ACTION = 'contact';
const RECAPTCHA_TIMEOUT_MS = 12000;
const REQUEST_TIMEOUT_MS = 20000;

let recaptchaLoader = null;

function loadRecaptchaV3(siteKey) {
  if (!siteKey) {
    return Promise.reject(new Error('Missing reCAPTCHA site key'));
  }

  if (typeof window !== 'undefined' && window.grecaptcha?.execute) {
    return new Promise((resolve) => {
      window.grecaptcha.ready(() => resolve(window.grecaptcha));
    });
  }

  if (recaptchaLoader) {
    return recaptchaLoader;
  }

  recaptchaLoader = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-recaptcha-v3]');
    if (existing) {
      existing.addEventListener('load', () => {
        if (window.grecaptcha) {
          window.grecaptcha.ready(() => resolve(window.grecaptcha));
        } else {
          reject(new Error('reCAPTCHA failed to initialize'));
        }
      });
      existing.addEventListener('error', () => reject(new Error('Failed to load reCAPTCHA')));
      return;
    }

    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
    script.async = true;
    script.defer = true;
    script.dataset.recaptchaV3 = 'true';
    script.onload = () => {
      if (!window.grecaptcha) {
        reject(new Error('reCAPTCHA failed to initialize'));
        return;
      }
      window.grecaptcha.ready(() => resolve(window.grecaptcha));
    };
    script.onerror = () => reject(new Error('Failed to load reCAPTCHA'));
    document.head.appendChild(script);
  }).catch((error) => {
    recaptchaLoader = null;
    throw error;
  });

  return recaptchaLoader;
}

async function getRecaptchaToken(siteKey, action, timeoutMs) {
  const grecaptcha = await Promise.race([
    loadRecaptchaV3(siteKey),
    new Promise((_, reject) => {
      setTimeout(() => reject(new Error('reCAPTCHA load timeout')), timeoutMs);
    }),
  ]);

  const token = await Promise.race([
    grecaptcha.execute(siteKey, { action }),
    new Promise((_, reject) => {
      setTimeout(() => reject(new Error('reCAPTCHA execute timeout')), timeoutMs);
    }),
  ]);

  if (!token || typeof token !== 'string') {
    throw new Error('Empty reCAPTCHA token');
  }

  return token;
}

const Contact = () => {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    website: '',
  });
  const [formStatus, setFormStatus] = useState({
    submitted: false,
    success: false,
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const loadingRef = useRef(false);
  const submitInFlightRef = useRef(false);
  const { theme } = useTheme();

  useEffect(() => {
    const subjectParam = searchParams.get('subject');
    if (subjectParam) {
      setFormData((prev) => ({
        ...prev,
        subject: prev.subject || decodeURIComponent(subjectParam),
      }));
    }
  }, [searchParams]);

  // Warm the v3 script so submit stays snappy (no user-facing challenge)
  useEffect(() => {
    if (!RECAPTCHA_SITE_KEY) {
      return undefined;
    }
    loadRecaptchaV3(RECAPTCHA_SITE_KEY).catch(() => {
      // Submit path will surface a clear error if load still fails
    });
    return undefined;
  }, []);

  const setLoadingState = (next) => {
    loadingRef.current = next;
    setLoading(next);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      subject: '',
      message: '',
      website: '',
    });
  };

  const submitForm = async (recaptchaToken) => {
    if (submitInFlightRef.current) {
      return;
    }
    submitInFlightRef.current = true;

    const submitData = {
      name: formData.name.trim(),
      email: formData.email.trim(),
      subject: formData.subject.trim(),
      message: formData.message.trim(),
      website: formData.website,
      'g-recaptcha-response': recaptchaToken,
      recaptcha_action: RECAPTCHA_ACTION,
    };

    const isDevelopment =
      import.meta.env.DEV ||
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1';
    const endpoint = isDevelopment
      ? 'http://localhost:8000/contact-handler.php'
      : '/contact-handler.php';

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(submitData),
        signal: controller.signal,
      });

      let result = {};
      try {
        result = await response.json();
      } catch {
        result = {};
      }

      if (response.ok && result.success && result.email_sent !== false) {
        setFormStatus({
          submitted: true,
          success: true,
          message: result.message || 'Thank you! Your message has been sent.',
        });
        resetForm();
        return;
      }

      setFormStatus({
        submitted: true,
        success: false,
        message:
          result.message ||
          `There was an error submitting the form. Please try again or email ${CONTACT_EMAIL}.`,
      });
    } catch (error) {
      if (isDevelopment && error.name !== 'AbortError') {
        setFormStatus({
          submitted: true,
          success: false,
          message: `Local PHP handler unreachable at ${endpoint}. Start it with: php -S localhost:8000 -t public`,
        });
        return;
      }

      if (error.name === 'AbortError') {
        setFormStatus({
          submitted: true,
          success: false,
          message: `Request timeout. Please try again or email us directly at ${CONTACT_EMAIL}`,
        });
      } else {
        setFormStatus({
          submitted: true,
          success: false,
          message: `Network error. Please try again or contact us directly at ${CONTACT_EMAIL}`,
        });
      }
    } finally {
      clearTimeout(timeoutId);
      submitInFlightRef.current = false;
      setLoadingState(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (loadingRef.current || submitInFlightRef.current) {
      return;
    }

    if (formData.message.trim().length < 10) {
      setFormStatus({
        submitted: true,
        success: false,
        message: 'Please provide a bit more detail in your message (at least 10 characters).',
      });
      return;
    }

    if (!RECAPTCHA_SITE_KEY) {
      setFormStatus({
        submitted: true,
        success: false,
        message: `Form security is not configured. Please email ${CONTACT_EMAIL} directly.`,
      });
      return;
    }

    setLoadingState(true);
    setFormStatus({ submitted: false, success: false, message: '' });

    try {
      const token = await getRecaptchaToken(
        RECAPTCHA_SITE_KEY,
        RECAPTCHA_ACTION,
        RECAPTCHA_TIMEOUT_MS
      );
      await submitForm(token);
    } catch (error) {
      console.error('reCAPTCHA error:', error);
      setLoadingState(false);
      setFormStatus({
        submitted: true,
        success: false,
        message: `Security check failed. Please try again or email ${CONTACT_EMAIL}.`,
      });
    }
  };

  return (
    <div className={theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'}>
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className={`text-4xl md:text-5xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-6`}>
            Start The Conversation
          </h1>
          <p className={`text-xl ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} mb-6`}>
            Brief us on the mandate and constraints. An initial conversation to understand what counsel would be useful.
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 pb-20">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
            <div className={`${theme === 'dark' ? 'bg-gray-800/80' : 'bg-white'} p-8 rounded-lg shadow-md h-fit`}>
              <EngagementEntryDiagram theme={theme} />
            </div>

            <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} p-8 rounded-lg shadow-md`}>
              <h2 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-6`}>
                Send a Message
              </h2>

              {formStatus.submitted && formStatus.success ? (
                <div className={`p-4 rounded-md ${theme === 'dark' ? 'bg-green-800 text-green-100' : 'bg-green-100 text-green-800'} mb-6`}>
                  <p>{formStatus.message}</p>
                  <button
                    type="button"
                    onClick={() => setFormStatus({ submitted: false, success: false, message: '' })}
                    className={`mt-4 text-sm underline ${theme === 'dark' ? 'text-green-200 hover:text-white' : 'text-green-800 hover:text-green-950'}`}
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form name="contact" onSubmit={handleSubmit} className="space-y-5">
                  {formStatus.submitted && !formStatus.success && (
                    <div className={`p-4 rounded-md ${theme === 'dark' ? 'bg-red-800 text-red-100' : 'bg-red-100 text-red-800'} mb-6`}>
                      <p>{formStatus.message}</p>
                    </div>
                  )}
                  <div>
                    <label
                      htmlFor="name"
                      className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-1`}
                    >
                      Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      maxLength={200}
                      autoComplete="name"
                      className={`w-full px-4 py-3 border rounded-md ${theme === 'dark'
                        ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500 focus:border-blue-500'
                        : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                        } transition-colors`}
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="email"
                      className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-1`}
                    >
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      maxLength={254}
                      autoComplete="email"
                      className={`w-full px-4 py-3 border rounded-md ${theme === 'dark'
                        ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500 focus:border-blue-500'
                        : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                        } transition-colors`}
                      placeholder="your.email@company.com"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="subject"
                      className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-1`}
                    >
                      Subject
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      maxLength={300}
                      className={`w-full px-4 py-3 border rounded-md ${theme === 'dark'
                        ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500 focus:border-blue-500'
                        : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                        } transition-colors`}
                      placeholder="What mandate should we review?"
                    />
                  </div>
                  <div className="absolute opacity-0 -z-10 h-0 overflow-hidden" aria-hidden="true">
                    <label htmlFor="website">Website</label>
                    <input
                      type="text"
                      id="website"
                      name="website"
                      value={formData.website}
                      onChange={handleChange}
                      autoComplete="off"
                      tabIndex={-1}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="message"
                      className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-1`}
                    >
                      Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      minLength={10}
                      maxLength={10000}
                      rows="5"
                      className={`w-full px-4 py-3 border rounded-md ${theme === 'dark'
                        ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500 focus:border-blue-500'
                        : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                        } transition-colors`}
                      placeholder="Describe the mandate, current constraints, and desired outcome."
                    ></textarea>
                  </div>
                  <div className="pt-2">
                    <button
                      type="submit"
                      disabled={loading}
                      className={`inline-flex items-center justify-center w-full px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white ${loading
                        ? 'bg-blue-400 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                        } transition-colors`}
                    >
                      {loading ? 'Sending...' : 'Send Message'}
                    </button>
                    <p className={`text-xs text-center mt-3 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                      You can also reach us at{' '}
                      <a
                        href={`mailto:${CONTACT_EMAIL}`}
                        className={`underline ${theme === 'dark' ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'}`}
                      >
                        {CONTACT_EMAIL}
                      </a>
                      .
                    </p>
                    <p className={`text-[11px] text-center mt-2 leading-relaxed ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
                      This site is protected by reCAPTCHA and the Google{' '}
                      <a
                        href="https://policies.google.com/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline"
                      >
                        Privacy Policy
                      </a>{' '}
                      and{' '}
                      <a
                        href="https://policies.google.com/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline"
                      >
                        Terms of Service
                      </a>{' '}
                      apply.
                    </p>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
